import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy } from 'lucide-react';
import { toast } from 'react-toastify';

interface PropertySharingModalProps {
  property: any;
  isOpen: boolean;
  onClose: () => void;
}

const PropertySharingModal: React.FC<PropertySharingModalProps> = ({ property, isOpen, onClose }) => {
  const propertyUrl = window.location.href;
  const propertyTitle = property.title;
  
  const shareLinks = [
    {
      name: "Facebook",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(propertyUrl)}`,
      color: "bg-blue-600 hover:bg-blue-700"
    },
    {
      name: "Twitter",
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(propertyUrl)}&text=${encodeURIComponent(`${propertyTitle} - Molinaro Immobiliare`)}`,
      color: "bg-blue-400 hover:bg-blue-500"
    },
    {
      name: "WhatsApp",
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${propertyTitle} - ${propertyUrl}`)}`,
      color: "bg-green-500 hover:bg-green-600"
    },
    {
      name: "LinkedIn",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(propertyUrl)}`,
      color: "bg-blue-700 hover:bg-blue-800"
    },
    {
      name: "Email",
      url: `mailto:?subject=${encodeURIComponent(`${propertyTitle} - Molinaro Immobiliare`)}&body=${encodeURIComponent(`Guarda questo immobile: ${propertyTitle}\n\n${propertyUrl}`)}`,
      color: "bg-red-500 hover:bg-red-600"
    }
  ];

  const copyToClipboard = () => {
    navigator.clipboard.writeText(propertyUrl);
    toast.success("Link copiato negli appunti");
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800">Condividi Immobile</h3>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full"
                aria-label="Chiudi"
              >
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 mb-4">Condividi questo immobile:</p>
              <div className="flex flex-wrap gap-4 justify-center">
                {shareLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer" 
                    onClick={() => setTimeout(onClose, 500)}
                    className={`${link.color} text-white py-3 px-6 rounded-lg transition-colors hover:shadow-md flex-1 text-center`}
                    title={`Condividi su ${link.name}`}
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-gray-600 mb-2">Link all'immobile:</p>
              <div className="flex items-center">
                <input
                  type="text"
                  value={propertyUrl}
                  readOnly
                  className="flex-1 border border-gray-300 rounded-l-lg py-2 px-4 text-gray-700 text-sm bg-gray-50"
                />
                <button
                  onClick={copyToClipboard}
                  className="bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-r-lg transition-colors"
                  title="Copia link"
                >
                  Copia
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PropertySharingModal;